/**
 * "Consulta" na aba Cliente do painel do atendimento (#1187): as consultas que
 * vão acontecer, das oportunidades do cliente. Lê a mesma chave que o
 * agendamento grava, então a consulta aparece assim que é marcada.
 */

import type { ReactNode } from 'react';
import { useConsultasDasOportunidades } from '@/shared/api/consultas';
import { Botao, Selo } from '@/shared/ui';
import type { AcaoNaConsulta } from './agendar-consulta';
import { consultasDoPainel } from '../lib/consultas-do-painel';
import './consultas-no-painel.css';

export function ConsultasNoPainel({
  oportunidades,
  moldura,
  aoAbrir,
  podeCancelar = false,
}: {
  oportunidades: readonly { readonly id: string }[];
  /** O bloco recolhível do painel; sem consulta, nada aparece. */
  moldura: (conteudo: ReactNode) => ReactNode;
  /**
   * O "Editar" e o "Cancelar agendamento" da consulta marcada (#1187): abrem o
   * painel lateral expandido. Sem ele (quem não agenda), só a consulta.
   */
  aoAbrir?: (acao: AcaoNaConsulta) => void;
  /** Com a parte de cancelar ligada na agenda da clínica. */
  podeCancelar?: boolean;
}): ReactNode {
  const leituras = useConsultasDasOportunidades(oportunidades.map((o) => o.id));
  const consultas = consultasDoPainel(
    oportunidades.map((o, i) => ({ oportunidadeId: o.id, consulta: leituras[i]?.data })),
    new Date(),
  );
  if (consultas.length === 0) return null;
  return moldura(
    <ul className="consultas-no-painel">
      {consultas.map((c) => (
        <li key={c.oportunidadeId} className="consultas-no-painel__item">
          <p className="consultas-no-painel__quando">{c.quando}</p>
          {(c.medico || c.especialidade) && (
            <p className="consultas-no-painel__quem">
              {[c.medico, c.especialidade].filter(Boolean).join(' · ')}
            </p>
          )}
          <Selo tom={c.marcada ? 'ok' : 'neutro'}>
            {c.marcada ? 'Marcada no SISAC' : 'Não marcada no SISAC'}
          </Selo>
          {c.marcada && aoAbrir && (
            <div className="consultas-no-painel__acoes">
              <Botao
                variante="secundario"
                miudo
                aria-label={`Editar a consulta de ${c.quando}`}
                onClick={() => aoAbrir({ modo: 'editar', oportunidadeId: c.oportunidadeId })}
              >
                Editar
              </Botao>
              {podeCancelar && (
                <Botao
                  variante="perigo"
                  miudo
                  aria-label={`Cancelar o agendamento de ${c.quando}`}
                  onClick={() => aoAbrir({ modo: 'cancelar', oportunidadeId: c.oportunidadeId })}
                >
                  Cancelar agendamento
                </Botao>
              )}
            </div>
          )}
        </li>
      ))}
    </ul>,
  );
}
